import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

import { CreateCityRequest } from "../../interfaces";
import { ActivityButton } from "../../components/Common";
import { GovernorateSelect } from "../../components";
import {
  useGetCityQuery,
  useAddCityMutation,
  useUpdateCityMutation,
} from "../../store/api/cityApi";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditCityModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateCityRequest>();

  const { data: city, error: getCityError } = useGetCityQuery(id as string, {
    skip: !id,
  });
  const [addCity, { isLoading: isAdding }] = useAddCityMutation();
  const [updateCity, { isLoading: isUpdating }] = useUpdateCityMutation();

  const [submitError, setSubmitError] = useState<string | null>(null);

  //#region form
  const form = useFormik<CreateCityRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      governorateId: request?.governorateId || "",
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            })
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            })
          ),
      }),
      governorateId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "governorate",
        })
      ),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitError(null);
        if (id) {
          await updateCity({ id, data: values }).unwrap();
        } else {
          await addCity(values).unwrap();
        }
        onClose();
      } catch (err: any) {
        setSubmitError(err?.data?.message || "An error occurred");
      }
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    setSubmitError(null);
    onCloseClick(!show);
  };

  useEffect(() => {
    if (id && city) {
      form.setValues({ name: city.name, governorateId: city.governorateId });
    }
  }, [city]);

  //#endregion

  return (
    <Modal
      id="addEdit"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {id
            ? t("editItem", { item: "city" })
            : t("addItem", { item: "city" })}
        </ModalHeader>
        {typeof submitError === "string" && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">{submitError}</p>
          </Alert>
        )}
        {typeof getCityError === "string" && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">{getCityError}</p>
          </Alert>
        )}
        <ModalBody>
          <div className="mb-3">
            <Label htmlFor="governorateId" className="form-label">
              {t("governorate")}
              <span className="text-danger">*</span>
            </Label>
            <GovernorateSelect
              type="select"
              id="governorateId"
              name="governorateId"
              className="form-select"
              aria-label={t("selectGovernorate")}
              placeholder={t("selectGovernorate")}
              value={form.values.governorateId}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.governorateId && form.errors.governorateId
                  ? true
                  : false
              }
            ></GovernorateSelect>
            {form.touched.governorateId && form.errors.governorateId ? (
              <FormFeedback type="invalid">
                {form.errors.governorateId}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="name.en" className="form-label">
              {t("nameEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              disabled={!!getCityError}
              autoComplete="off"
              id="name.en"
              name="name.en"
              value={form.values?.name.en}
              type="text"
              className="form-control"
              placeholder={t("enterInEnglish")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.en && form.errors.name?.en ? true : false
              }
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="name.ar" className="form-label">
              {t("nameAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              disabled={!!getCityError}
              autoComplete="off"
              auto
              id="name.ar"
              name="name.ar"
              value={form.values.name?.ar}
              type="text"
              className="form-control"
              placeholder={t("enterInArabic")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.ar && form.errors.name?.ar ? true : false
              }
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={isAdding || isUpdating}
            disabled={isAdding || isUpdating || !!getCityError}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
